import React, { useState } from "react";
import {
  Grid,
  TextField,
  InputLabel,
  Divider,
  Checkbox,
  FormControlLabel,
  Button,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Typography,
  IconButton,
  Input,
  Autocomplete,
  Tooltip,
  Chip,
  Stack,
  Box,
  CircularProgress,
  Link,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import {
  Remove as RemoveIcon,
  Add as AddIcon,
  AddBox as AddBoxIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  OpenInBrowser as OpenInBrowserIcon,
} from "@mui/icons-material";

import fallbackImage from "../../../../assets/images/noimage.png";
import ProductImageCropDialog from "./ProductImageCropDialog";

const useStyles = makeStyles((theme) => ({
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(3),
  },
}));

const ProductForm = (props) => {
  const {
    product,
    warehouses,
    categories,
    handleChange,
    overriddenEndorment,
    overriddenStyle,
    toggleProductModal,
    productId,
    handleCdnUpload,
    handleCdnRemove,
  } = props;

  const classes = useStyles();

  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [cdnUploading, setCdnUploading] = useState(false);

  const handleCdnFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result);
      setCropDialogOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropConfirm = async (blob) => {
    setCropDialogOpen(false);
    setCropImageSrc(null);
    setCdnUploading(true);
    await handleCdnUpload(blob);
    setCdnUploading(false);
  };

  const handleCropDialogClose = () => {
    setCropDialogOpen(false);
    setCropImageSrc(null);
  };

  const onMediaFallback = (e) => (e.target.src = fallbackImage);

  return (
    <form margin="dense" noValidate autoComplete="off" className={classes.form}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={12}>
          <div style={{ display: "flex", gap: 8 }}>
            <TextField
              InputProps={{
                inputProps: {
                  autoComplete: "off",
                  readOnly: true,
                },
              }}
              size="small"
              variant="outlined"
              label="Item Code Description "
              name="name"
              value={product && product.item_code.name}
              style={{ background: "lightgray", flex: 1 }}
            />
            <Link
              href={`/itemcodes/${product?.item_code?.item_code}`}
              target="_blank"
            >
              <Tooltip title="Edit item code">
                <IconButton aria-label="edit item code" color="primary">
                  <OpenInBrowserIcon />
                </IconButton>
              </Tooltip>
            </Link>
          </div>
        </Grid>
        <Grid item xs={12} sm={12}>
          <TextField
            fullWidth
            InputProps={{
              inputProps: {
                autoComplete: "off",
              },
            }}
            size="small"
            variant="outlined"
            label="Human Description "
            name="name"
            value={product && product.name}
            onChange={handleChange}
          />
        </Grid>
        {product.api_managed ? (
          <Grid item xs={12} sm={12}>
            <TextField
              fullWidth
              InputProps={{
                inputProps: {
                  autoComplete: "off",
                },
              }}
              size="small"
              variant="outlined"
              label="Product Description "
              name="api_description"
              value={product && product.api_description}
              onChange={handleChange}
            />
          </Grid>
        ) : (
          <></>
        )}
        <Grid item xs={12} sm={6}>
          <InputLabel shrink id="warehouseLabel">
            Warehouse
          </InputLabel>
          <Autocomplete
            fullWidth
            name="warehouse"
            input={<Input fullWidth />}
            value={product && product.warehouse}
            options={warehouses}
            getOptionLabel={(option) => `${option.name}`}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                variant="standard"
                helperText={
                  product.warehouse === null ? (
                    <span style={{ color: "red" }}>
                      This field is required.
                    </span>
                  ) : (
                    <></>
                  )
                }
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionDisabled={(option) => option.id === 2}
            onChange={(e, v) =>
              handleChange(
                {
                  target: {
                    checked: false,
                    name: `warehouse`,
                    value: product.warehouse,
                  },
                },
                v,
              )
            }
            readOnly={product.api_managed}
            disabled={product.api_managed}
          />
        </Grid>
        {categories.length > 0 && (
          <Grid item xs={12} sm={6}>
            <InputLabel shrink id="categoryLabel">
              FF Department
            </InputLabel>
            <Autocomplete
              fullWidth
              name="category"
              input={<Input fullWidth />}
              value={product && product.category}
              options={categories}
              getOptionLabel={(option) => `${option.name}`}
              renderOption={(props, option) => (
                <li
                  {...props}
                  key={option.id}
                  style={{
                    paddingLeft: `${16 + option.depth * 20}px`,
                    color:
                      option.depth === 0
                        ? "#000"
                        : option.depth === 1
                          ? "#333"
                          : "#666",
                    fontSize: `${14 - option.depth * 1}px`,
                    fontWeight:
                      option.depth === 0 ? 600 : option.depth === 1 ? 500 : 400,
                  }}
                >
                  <span
                    style={{
                      marginRight: 8,
                      opacity: 0.6,
                      fontWeight: 400,
                    }}
                  >
                    {option.depth === 1 ? "→ " : option.depth === 2 ? "↳ " : ""}
                  </span>
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} size="small" variant="standard" />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(e, v) =>
                handleChange(
                  {
                    target: {
                      checked: false,
                      name: `category`,
                      value: product.category,
                    },
                  },
                  v,
                )
              }
            />
          </Grid>
        )}
        <Grid item xs={12} sm={12}>
          <TextField
            fullWidth
            InputProps={{
              inputProps: {
                autoComplete: "off",
              },
              endAdornment: overriddenEndorment("bin", product),
            }}
            size="small"
            variant="outlined"
            label="Bin "
            name="bin"
            value={product.bin}
            onChange={handleChange}
            style={{
              background: overriddenStyle("bin", product),
            }}
          />
        </Grid>
        {product.upc ? (
          <Grid item xs={12} sm={12}>
            <TextField
              fullWidth
              InputProps={{
                inputProps: {
                  autoComplete: "off",
                },
              }}
              size="small"
              variant="outlined"
              label="UPC: "
              name="upc"
              value={product && product.upc}
              onChange={handleChange}
            />
          </Grid>
        ) : (
          <></>
        )}
        <Grid item xs={12} sm={12}>
          <TextField
            fullWidth
            InputProps={{
              inputProps: {
                autoComplete: "off",
              },
              endAdornment: overriddenEndorment("item_code", product),
            }}
            size="small"
            variant="outlined"
            label="Item Code "
            name="item_code"
            value={(product && product.item_code.item_code) || ""}
            onChange={handleChange}
            style={{
              background: overriddenStyle("item_code", product),
            }}
            helperText={
              product.item_code.item_code === "" ? (
                <span style={{ color: "red" }}>This field is required.</span>
              ) : (
                <></>
              )
            }
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <Divider />
        </Grid>
        <Grid item xs={12} sm={12}>
          <InputLabel shrink id="unitTypeLabel">
            Unit Type
          </InputLabel>
          <FormControlLabel
            value="start"
            control={
              <Checkbox
                name="unit_type_unit"
                value={product && product.unit_type_unit}
                checked={product && product.unit_type_unit}
                onChange={handleChange}
                color="primary"
              />
            }
            label="Unit"
            labelPlacement="end"
          />
          <br />
          <FormControlLabel
            value="start"
            control={
              <Checkbox
                name="unit_type_case"
                value={product && product.unit_type_case}
                checked={product && product.unit_type_case}
                onChange={handleChange}
                color="primary"
              />
            }
            label="Case"
            labelPlacement="end"
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <Button
            name="item_type_non_weighted"
            variant="text"
            color={
              product &&
              (product.item_type === "" || product.item_type === "non_weighted"
                ? "primary"
                : "inherit")
            }
            onClick={handleChange}
          >
            Non weighted
          </Button>
          <Button
            name="item_type_catch_weight"
            variant="text"
            color={
              product && product.item_type === "catch_weight"
                ? "primary"
                : "inherit"
            }
            onClick={handleChange}
          >
            Catch weight
          </Button>
          <Button
            name="item_type_fixed_random_weight"
            variant="text"
            color={
              product && product.item_type === "fixed_random_weight"
                ? "primary"
                : "inherit"
            }
            onClick={handleChange}
          >
            Fixed random weight
          </Button>
          <Grid item xs={12} sm={12}>
            <Grid container spacing={3} alignItems="center">
              {product &&
              (product.item_type === "" ||
                product.item_type === "non_weighted") ? (
                <>
                  {product.unit_type_unit ? (
                    <Grid item xs={12} sm={12}>
                      <TextField
                        fullWidth
                        InputProps={{
                          inputProps: { min: 0 },
                          endAdornment: overriddenEndorment(
                            "unit_price",
                            product,
                          ),
                        }}
                        size="small"
                        variant="outlined"
                        label="Unit Price"
                        name="unit_price"
                        value={product && product.unit_price}
                        type="number"
                        disabled={product && !product.unit_type_unit}
                        onChange={handleChange}
                        style={{
                          background: overriddenStyle("unit_price", product),
                        }}
                      />
                    </Grid>
                  ) : (
                    <></>
                  )}
                  {product.unit_type_case ? (
                    <>
                      <Grid item xs={"auto"} sm={"auto"}>
                        <TextField
                          InputProps={{
                            inputProps: { min: 0 },
                            endAdornment: overriddenEndorment(
                              "case_price",
                              product,
                            ),
                          }}
                          size="small"
                          variant="outlined"
                          label="Case Price"
                          name="case_price"
                          value={product && product.case_price}
                          type="number"
                          disabled={product && !product.unit_type_case}
                          onChange={handleChange}
                          style={{
                            background: overriddenStyle("case_price", product),
                          }}
                        />
                      </Grid>
                      <Grid item xs={"auto"} sm={"auto"}>
                        <b>=</b>
                      </Grid>
                      <Grid item xs={"auto"} sm={"auto"}>
                        <TextField
                          InputProps={{
                            inputProps: { min: 0 },
                            endAdornment: overriddenEndorment(
                              "units_per_case",
                              product,
                            ),
                          }}
                          size="small"
                          variant="outlined"
                          label="Units per Case"
                          name="units_per_case"
                          value={product && product.units_per_case}
                          type="number"
                          disabled={product && !product.unit_type_case}
                          onChange={handleChange}
                          style={{
                            background: overriddenStyle(
                              "units_per_case",
                              product,
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={"auto"} sm={"auto"}>
                        <b>x</b>
                      </Grid>
                      <Grid item xs={"auto"} sm={"auto"}>
                        <TextField
                          fullWidth
                          InputProps={{
                            inputProps: {
                              readOnly: true,
                            },
                          }}
                          size="small"
                          variant="outlined"
                          label="Price per Unit"
                          name="price_per_unit"
                          value={product && product.price_per_unit}
                          type="number"
                          onChange={handleChange}
                        />
                      </Grid>
                    </>
                  ) : (
                    <></>
                  )}
                </>
              ) : product &&
                (product.item_type === "catch_weight" ||
                  product.item_type === "fixed_random_weight") ? (
                <>
                  <Grid item xs={12} sm={12}>
                    <Grid container spacing={3} alignItems="center">
                      <Grid item xs={3} sm={3}>
                        {product.unit_type_unit ? (
                          <TextField
                            InputProps={{
                              inputProps: { min: 0 },
                              endAdornment: overriddenEndorment(
                                "unit_price",
                                product,
                              ),
                            }}
                            size="small"
                            variant="outlined"
                            label="Unit price per lb"
                            name="unit_price"
                            value={product && product.unit_price}
                            type="number"
                            onChange={handleChange}
                            style={{
                              background: overriddenStyle(
                                "unit_price",
                                product,
                              ),
                            }}
                          />
                        ) : (
                          <></>
                        )}
                        {product.unit_type_unit && product.unit_type_case ? (
                          <>
                            <br />
                            <br />
                            <br />
                          </>
                        ) : (
                          <></>
                        )}
                        {product.unit_type_case ? (
                          <TextField
                            InputProps={{
                              inputProps: { min: 0 },
                              endAdornment: overriddenEndorment(
                                "case_price",
                                product,
                              ),
                            }}
                            size="small"
                            variant="outlined"
                            label="Case price per lb"
                            name="case_price"
                            value={product && product.case_price}
                            type="number"
                            onChange={handleChange}
                            style={{
                              background: overriddenStyle(
                                "case_price",
                                product,
                              ),
                            }}
                          />
                        ) : (
                          <></>
                        )}
                      </Grid>
                      <Grid item xs={true} sm={true}>
                        <Grid container spacing={3} alignItems="center">
                          {product.unit_type_unit ? (
                            <>
                              <Grid item xs={"auto"} sm={"auto"}>
                                <b>x</b>
                              </Grid>
                              <Grid item xs={"auto"} sm={"auto"}>
                                <TextField
                                  InputProps={{
                                    inputProps: { min: 0 },
                                    endAdornment: overriddenEndorment(
                                      "average_unit_weight",
                                      product,
                                    ),
                                  }}
                                  size="small"
                                  variant="outlined"
                                  label="Average Unit Weight"
                                  name="average_unit_weight"
                                  value={product && product.average_unit_weight}
                                  type="number"
                                  onChange={handleChange}
                                  style={{
                                    background: overriddenStyle(
                                      "average_unit_weight",
                                      product,
                                    ),
                                  }}
                                />
                              </Grid>
                              <Grid item xs={"auto"} sm={"auto"}>
                                <b>=</b>
                              </Grid>
                              <Grid item xs={"auto"} sm={"auto"}>
                                <TextField
                                  InputProps={{
                                    inputProps: {
                                      readOnly: true,
                                    },
                                  }}
                                  size="small"
                                  variant="outlined"
                                  label="Average Unit Price"
                                  name="average_unit_price"
                                  value={product && product.average_unit_price}
                                  type="number"
                                />
                              </Grid>
                            </>
                          ) : (
                            <></>
                          )}
                          {product.unit_type_unit && product.unit_type_case ? (
                            <Grid item xs={12} sm={12}></Grid>
                          ) : (
                            <></>
                          )}
                          {product.unit_type_case ? (
                            <>
                              <Grid item xs={"auto"} sm={"auto"}>
                                <b>x</b>
                              </Grid>
                              <Grid item xs={"auto"} sm={"auto"}>
                                <TextField
                                  InputProps={{
                                    inputProps: { min: 0 },
                                    endAdornment: overriddenEndorment(
                                      "average_case_weight",
                                      product,
                                    ),
                                  }}
                                  size="small"
                                  variant="outlined"
                                  label="Average Case Weight"
                                  name="average_case_weight"
                                  value={product && product.average_case_weight}
                                  type="number"
                                  onChange={handleChange}
                                  style={{
                                    background: overriddenStyle(
                                      "average_case_weight",
                                      product,
                                    ),
                                  }}
                                />
                              </Grid>
                              <Grid item xs={"auto"} sm={"auto"}>
                                <b>=</b>
                              </Grid>
                              <Grid item xs={"auto"} sm={"auto"}>
                                <TextField
                                  InputProps={{
                                    inputProps: {
                                      autoComplete: "off",
                                      readOnly: true,
                                    },
                                  }}
                                  size="small"
                                  variant="outlined"
                                  label="Average Case Price"
                                  name="average_case_price"
                                  value={product && product.average_case_price}
                                  type="number"
                                />
                              </Grid>
                            </>
                          ) : (
                            <></>
                          )}
                        </Grid>
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <Divider />
                      </Grid>
                      <Grid item xs={"auto"} sm={"auto"}>
                        <TextField
                          InputProps={{
                            inputProps: { min: 0 },
                            endAdornment: overriddenEndorment(
                              "volume_level",
                              product,
                            ),
                          }}
                          size="small"
                          variant="outlined"
                          label="Volume Level"
                          name="volume_level"
                          value={product && product.volume_level}
                          type="number"
                          onChange={handleChange}
                          style={{
                            marginTop: 24,
                            background: overriddenStyle(
                              "volume_level",
                              product,
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={"auto"} sm={"auto"}>
                        <TextField
                          InputProps={{
                            inputProps: { min: 0 },
                            endAdornment: overriddenEndorment(
                              "volume_price",
                              product,
                            ),
                          }}
                          size="small"
                          variant="outlined"
                          label="Volume Price"
                          name="volume_price"
                          value={product && product.volume_price}
                          type="number"
                          onChange={handleChange}
                          style={{
                            marginTop: 24,
                            background: overriddenStyle(
                              "volume_price",
                              product,
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </>
              ) : (
                <></>
              )}
              <Grid item xs={12} sm={12}>
                <Divider />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Card sx={{ maxWidth: 345 }}>
                  <CardActionArea style={{ textAlign: "center" }}>
                    <CardMedia
                      component="img"
                      style={{
                        width: "auto",
                        height: 140,
                        display: "inline-block",
                      }}
                      image={
                        product.custom_image || product.url || fallbackImage
                      }
                      alt={product.name || ""}
                      onError={onMediaFallback}
                    />
                  </CardActionArea>
                  <CardContent>
                    <Grid container alignItems="center">
                      <Grid item xs={12}>
                        <Typography
                          gutterBottom
                          component="div"
                          align="left"
                          className="typographyCode"
                        >
                          {product.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          align="left"
                        >
                          {product.item_code ? product.item_code.item_code : ""}
                        </Typography>
                      </Grid>
                      {["Unit", "Unit / Case"].includes(product.unit_type) &&
                      product.unit_price > 0 ? (
                        <>
                          <Grid item xs={6}>
                            <Typography
                              align="left"
                              className="typographyPriceUnit"
                            >
                              $
                              {product.item_type === "catch_weight" ||
                              product.item_type === "fixed_random_weight"
                                ? product.average_unit_price
                                : product.unit_price}
                              <span style={{ fontWeight: 900 }}> UNIT</span>
                            </Typography>

                            {product.item_type === "catch_weight" ||
                            product.item_type === "fixed_random_weight" ? (
                              <Typography
                                align="left"
                                className="typographyPricePerLbUnit"
                              >
                                Unit price per lb:
                                {product.unit_price
                                  ? " $" + product.unit_price
                                  : " N/A"}
                              </Typography>
                            ) : (
                              <></>
                            )}
                          </Grid>
                          <Grid item xs={2}>
                            <IconButton>
                              <RemoveIcon color="error" />
                            </IconButton>
                          </Grid>
                          <Grid item xs={2}>
                            <TextField
                              fullWidth
                              size="small"
                              variant="standard"
                              name="unit_in_cart"
                              value={0}
                              inputProps={{
                                style: { textAlign: "center" },
                              }}
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <IconButton>
                              <AddIcon color="success" />
                            </IconButton>
                          </Grid>
                        </>
                      ) : (
                        <></>
                      )}
                      {["Case", "Unit / Case"].includes(product.unit_type) &&
                      product.case_price > 0 ? (
                        <>
                          <Grid item xs={6}>
                            <Typography
                              align="left"
                              className="typographyPriceCase"
                            >
                              $
                              {product.item_type === "catch_weight" ||
                              product.item_type === "fixed_random_weight"
                                ? product.average_case_price
                                : product.case_price}{" "}
                              ({product.units_per_case})
                              <span style={{ fontWeight: 900 }}> CASE</span>
                            </Typography>
                            {product.item_type === "catch_weight" ||
                            product.item_type === "fixed_random_weight" ? (
                              <Typography
                                align="left"
                                className="typographyPricePerLbCase"
                              >
                                Case price per lb:
                                {product.case_price
                                  ? " $" + product.case_price
                                  : " N/A"}
                              </Typography>
                            ) : (
                              <></>
                            )}
                          </Grid>
                          <Grid item xs={2}>
                            <IconButton>
                              <RemoveIcon color="error" />
                            </IconButton>
                          </Grid>
                          <Grid item xs={2}>
                            <TextField
                              fullWidth
                              size="small"
                              variant="standard"
                              name="case_in_cart"
                              value={0}
                              inputProps={{
                                style: { textAlign: "center" },
                              }}
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <IconButton>
                              <AddIcon color="success" />
                            </IconButton>
                          </Grid>
                        </>
                      ) : (
                        <></>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={12}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  {product.custom_image ? (
                    <>
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="CDN Active"
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ wordBreak: "break-all", maxWidth: 260 }}
                      >
                        {product.custom_image}
                      </Typography>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={handleCdnRemove}
                        disabled={cdnUploading}
                      >
                        Remove
                      </Button>
                      <Tooltip
                        title={
                          !productId
                            ? "Save the product first to upload an image"
                            : ""
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            component="label"
                            startIcon={<CloudUploadIcon />}
                            disabled={!productId || cdnUploading}
                          >
                            Replace
                            <input
                              hidden
                              accept="image/*"
                              type="file"
                              onChange={handleCdnFileSelect}
                            />
                          </Button>
                        </span>
                      </Tooltip>
                    </>
                  ) : product.url ? (
                    <>
                      <Chip
                        icon={<WarningIcon />}
                        label="Using external URL"
                        color="warning"
                        size="small"
                        variant="outlined"
                      />
                      <Tooltip
                        title={
                          !productId
                            ? "Save the product first to upload an image"
                            : ""
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            component="label"
                            startIcon={<CloudUploadIcon />}
                            disabled={!productId || cdnUploading}
                          >
                            Upload to CDN
                            <input
                              hidden
                              accept="image/*"
                              type="file"
                              onChange={handleCdnFileSelect}
                            />
                          </Button>
                        </span>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <Chip
                        label="No image configured"
                        size="small"
                        variant="outlined"
                      />
                      <Tooltip
                        title={
                          !productId
                            ? "Save the product first to upload an image"
                            : ""
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            component="label"
                            startIcon={<CloudUploadIcon />}
                            disabled={!productId || cdnUploading}
                          >
                            Setup CDN image
                            <input
                              hidden
                              accept="image/*"
                              type="file"
                              onChange={handleCdnFileSelect}
                            />
                          </Button>
                        </span>
                      </Tooltip>
                    </>
                  )}
                </Stack>
                {cdnUploading && (
                  <Box sx={{ mt: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={20} />
                      <Typography variant="caption">Uploading…</Typography>
                    </Stack>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} sm={12}>
                <Divider />
              </Grid>
              <Grid item xs={12} sx={{ textAlign: "center" }}>
                <Typography component="h4">
                  Price link
                  <Tooltip title="Price link allows you to link this product's price to an existing product. When enabled, the price of this product will automatically update based on the linked product.">
                    <IconButton>
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </Typography>
              </Grid>
              {!product.price_links?.length ? null : (
                <Grid item xs={12}>
                  <FormControlLabel
                    value="start"
                    control={
                      <Checkbox
                        name="enable_price_links"
                        value={product && product.enable_price_links}
                        checked={product && product.enable_price_links}
                        onChange={handleChange}
                        color="primary"
                      />
                    }
                    label="Enable price link"
                    labelPlacement="end"
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="body1" color="text.secondary">
                  Linked item:
                </Typography>
              </Grid>
              <Grid item xs={12} style={{ marginLeft: 8 }}>
                <Grid container spacing={2} alignItems="stretch">
                  {product.price_links_detail?.length > 0 ? (
                    product.price_links_detail.map((item, index) => (
                      <Grid item xs={"auto"} key={item.upc}>
                        <Tooltip title={item?.upc ? item.upc : item}>
                          <Card
                            sx={{
                              maxWidth: 150,
                              height: "100%",
                              border: `1px solid ${
                                item.default_option ? "lightblue" : "gray"
                              }`,
                              background: `${
                                item.default_option ? "#e0ffff" : "#eee"
                              }`,
                            }}
                          >
                            <CardContent>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {item?.item_code?.item_code
                                  ? item.item_code.item_code
                                  : item?.item_code}
                              </Typography>
                              {item?.name && (
                                <Typography>{item.name}</Typography>
                              )}
                              {item.display_unit_price !== null &&
                                item.display_unit_price > 0 && (
                                  <Typography
                                    align="left"
                                    className="typographyPriceUnit"
                                  >
                                    {`$${item.display_unit_price} `}
                                    <span style={{ fontWeight: 900 }}>
                                      UNIT
                                    </span>
                                  </Typography>
                                )}
                              {item.display_case_price !== null &&
                                item.display_case_price > 0 && (
                                  <Typography
                                    align="left"
                                    className="typographyPriceCase"
                                  >
                                    {`$${item.display_case_price} `}
                                    <span style={{ fontWeight: 900 }}>
                                      CASE
                                    </span>
                                  </Typography>
                                )}
                            </CardContent>
                            <CardActionArea
                              onClick={() =>
                                handleChange(
                                  {
                                    target: {
                                      name: "price_links",
                                      value: item.upc,
                                    },
                                  },
                                  item,
                                )
                              }
                              sx={{
                                textAlign: "center",
                                color: "text.secondary",
                                "&:hover": { color: "red" },
                              }}
                            >
                              <DeleteIcon />
                            </CardActionArea>
                          </Card>
                        </Tooltip>
                      </Grid>
                    ))
                  ) : (
                    <></>
                  )}
                  <Grid item xs={"auto"} alignSelf="center">
                    <Tooltip title="Add Item Link">
                      <IconButton onClick={toggleProductModal}>
                        <AddBoxIcon fontSize="large" color="primary" />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <ProductImageCropDialog
        open={cropDialogOpen}
        imageSrc={cropImageSrc}
        onClose={handleCropDialogClose}
        onConfirm={handleCropConfirm}
      />
    </form>
  );
};

export default ProductForm;
